import { useState } from 'react'
import {
  Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag,
  Typography,
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined } from '@ant-design/icons'
import {
  useDeviceProfiles,
  useCreateDeviceProfile,
  useUpdateDeviceProfile,
  useDeleteDeviceProfile,
} from '../hooks/useDeviceProfiles'
import type { DeviceProfileCreate, DeviceProfileUpdate, ServiceDefinition, ServiceProperty } from '../api/deviceProfiles'

const { Title, Text } = Typography

const PROPERTY_TYPES = [
  { value: 'enum', label: '枚举 (enum)' },
  { value: 'integer', label: '整数 (integer)' },
  { value: 'float', label: '浮点 (float)' },
  { value: 'boolean', label: '布尔 (boolean)' },
  { value: 'string', label: '字符串 (string)' },
]

// 空属性模板
function emptyProperty(): ServiceProperty {
  return { name: '', display_name: '', type: 'boolean', default: false }
}

// 空服务模板
function emptyService(): ServiceDefinition {
  return { name: '', description: '', properties: [emptyProperty()] }
}

export default function DeviceProfileManage() {
  const { data: profiles = [] } = useDeviceProfiles()
  const createMutation = useCreateDeviceProfile()
  const updateMutation = useUpdateDeviceProfile()
  const deleteMutation = useDeleteDeviceProfile()

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<DeviceProfileCreate | null>(null)
  const [form] = Form.useForm()

  // ── 打开创建弹窗 ───────────────────────────────────────────────
  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({
      category: 'actuator',
      services: [emptyService()],
    })
    setModalOpen(true)
  }

  // ── 打开编辑弹窗 ───────────────────────────────────────────────
  const openEdit = (profile: (typeof profiles)[0]) => {
    setEditing({ id: profile.id, display_name: profile.display_name, category: profile.category as 'actuator' | 'sensor', services: profile.services })
    form.setFieldsValue({
      display_name: profile.display_name,
      category: profile.category,
      services: profile.services.length > 0 ? profile.services : [emptyService()],
    })
    setModalOpen(true)
  }

  // ── 提交 ───────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const values = await form.validateFields()
    const services: ServiceDefinition[] = (values.services || []).map(
      (svc: ServiceDefinition) => ({
        ...svc,
        properties: (svc.properties || []).map((p: ServiceProperty) => ({
          ...p,
          default: p.default ?? (p.type === 'boolean' ? false : p.type === 'integer' || p.type === 'float' ? 0 : ''),
        })),
      })
    )

    if (editing) {
      const data: DeviceProfileUpdate = {
        display_name: values.display_name,
        category: values.category,
        services,
      }
      await updateMutation.mutateAsync({ id: editing.id, data })
    } else {
      const data: DeviceProfileCreate = {
        id: values.id,
        display_name: values.display_name,
        category: values.category,
        services,
      }
      await createMutation.mutateAsync(data)
    }
    setModalOpen(false)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>设备型号管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} loading={createMutation.isPending}>
          新增型号
        </Button>
      </div>

      <Table
        dataSource={profiles}
        rowKey="id"
        size="small"
        pagination={false}
        columns={[
          {
            title: 'ID', dataIndex: 'id', width: 160,
            render: (v: string) => <code>{v}</code>,
          },
          { title: '显示名称', dataIndex: 'display_name', width: 140 },
          {
            title: '类别', dataIndex: 'category', width: 80,
            render: (v: string) => (
              <Tag color={v === 'sensor' ? 'green' : 'blue'}>
                {v === 'sensor' ? '传感器' : '执行器'}
              </Tag>
            ),
          },
          {
            title: '服务数', width: 70, align: 'center',
            render: (_: unknown, r: (typeof profiles)[0]) => r.services.length,
          },
          {
            title: '属性数', width: 70, align: 'center',
            render: (_: unknown, r: (typeof profiles)[0]) =>
              r.services.reduce((acc, svc) => acc + svc.properties.length, 0),
          },
          {
            title: '创建时间', dataIndex: 'created_at', width: 160,
            render: (t: string) => new Date(t).toLocaleString('zh-CN'),
          },
          {
            title: '操作', width: 120,
            render: (_: unknown, r: (typeof profiles)[0]) => (
              <Space size="small">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => openEdit(r)}
                />
                <Popconfirm
                  title={`确定删除型号「${r.display_name}」？`}
                  description="已关联此型号的设备将不受影响。"
                  onConfirm={() => deleteMutation.mutate(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} loading={deleteMutation.isPending} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
        locale={{ emptyText: '暂无设备型号' }}
      />

      {/* ── 创建/编辑弹窗 ───────────────────────────────────────── */}
      <Modal
        title={editing ? `编辑型号：${editing.id}` : '新增设备型号'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {/* ID：创建时可输入，编辑时只读 */}
          <Form.Item
            name="id"
            label="型号 ID"
            rules={[
              { required: true, message: '请输入唯一标识' },
              { pattern: /^[a-z][a-z0-9_]*$/, message: '仅小写字母开头，可用字母/数字/下划线' },
            ]}
          >
            <Input placeholder="如：ac、thermostat、door_lock" disabled={!!editing} />
          </Form.Item>

          <Form.Item
            name="display_name"
            label="显示名称"
            rules={[{ required: true, message: '请输入显示名称' }]}
          >
            <Input placeholder="如：智能空调" />
          </Form.Item>

          <Form.Item name="category" label="设备类别">
            <Select
              options={[
                { value: 'actuator', label: '执行器（可主动控制）' },
                { value: 'sensor', label: '传感器（仅上报数据）' },
              ]}
            />
          </Form.Item>

          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            服务 & 属性定义
          </Text>
          <Form.List name="services">
            {(svcFields, { add: addSvc, remove: removeSvc }) => (
              <>
                {svcFields.map(({ key: svcKey, name: svcIdx, ...svcRest }) => (
                  <Card
                    key={svcKey}
                    size="small"
                    style={{ marginBottom: 12, background: '#fafafa' }}
                    title={
                      <Space>
                        <Form.Item
                          {...svcRest}
                          name={[svcIdx, 'name']}
                          rules={[{ required: true, message: '服务名' }]}
                          noStyle
                        >
                          <Input placeholder="服务名称（如 ACControl）" style={{ width: 200 }} />
                        </Form.Item>
                        <Form.Item {...svcRest} name={[svcIdx, 'description']} noStyle>
                          <Input placeholder="服务描述" style={{ width: 200 }} />
                        </Form.Item>
                      </Space>
                    }
                    extra={
                      svcFields.length > 1 && (
                        <Button
                          size="small"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => removeSvc(svcIdx)}
                        />
                      )
                    }
                  >
                    <Form.List name={[svcIdx, 'properties']}>
                      {(propFields, { add: addProp, remove: removeProp }) => (
                        <>
                          {propFields.map(({ key: propKey, name: propIdx, ...propRest }) => (
                            <Space key={propKey} align="start" style={{ display: 'flex', marginBottom: 8 }} wrap>
                              <Form.Item
                                {...propRest}
                                name={[propIdx, 'name']}
                                rules={[{ required: true, message: '属性名' }]}
                                style={{ marginBottom: 0 }}
                              >
                                <Input placeholder="属性名（英文）" style={{ width: 120 }} />
                              </Form.Item>
                              <Form.Item
                                {...propRest}
                                name={[propIdx, 'display_name']}
                                rules={[{ required: true, message: '显示名' }]}
                                style={{ marginBottom: 0 }}
                              >
                                <Input placeholder="显示名（中文）" style={{ width: 110 }} />
                              </Form.Item>
                              <Form.Item
                                {...propRest}
                                name={[propIdx, 'type']}
                                rules={[{ required: true }]}
                                style={{ marginBottom: 0 }}
                              >
                                <Select options={PROPERTY_TYPES} style={{ width: 130 }} />
                              </Form.Item>
                              <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                                {({ getFieldValue }) => {
                                  const propType = getFieldValue(['services', svcIdx, 'properties', propIdx, 'type'])
                                  if (propType === 'enum') {
                                    return (
                                      <Form.Item {...propRest} name={[propIdx, 'values']} style={{ marginBottom: 0 }}>
                                        <Select
                                          mode="tags"
                                          placeholder="可选值（回车添加）"
                                          style={{ width: 180 }}
                                        />
                                      </Form.Item>
                                    )
                                  }
                                  if (propType === 'integer' || propType === 'float') {
                                    return (
                                      <>
                                        <Form.Item {...propRest} name={[propIdx, 'min']} style={{ marginBottom: 0 }}>
                                          <Input placeholder="最小值" style={{ width: 70 }} />
                                        </Form.Item>
                                        <Form.Item {...propRest} name={[propIdx, 'max']} style={{ marginBottom: 0 }}>
                                          <Input placeholder="最大值" style={{ width: 70 }} />
                                        </Form.Item>
                                        <Form.Item {...propRest} name={[propIdx, 'unit']} style={{ marginBottom: 0 }}>
                                          <Input placeholder="单位" style={{ width: 50 }} />
                                        </Form.Item>
                                      </>
                                    )
                                  }
                                  return null
                                }}
                              </Form.Item>
                              <Form.Item {...propRest} name={[propIdx, 'default']} style={{ marginBottom: 0 }}>
                                <Input placeholder="默认值" style={{ width: 80 }} />
                              </Form.Item>
                              {propFields.length > 1 && (
                                <MinusCircleOutlined
                                  onClick={() => removeProp(propIdx)}
                                  style={{ color: '#ff4d4f', marginTop: 8 }}
                                />
                              )}
                            </Space>
                          ))}
                          <Button
                            type="dashed"
                            size="small"
                            onClick={() => addProp(emptyProperty())}
                            icon={<PlusOutlined />}
                          >
                            添加属性
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </Card>
                ))}
                <Button type="dashed" onClick={() => addSvc(emptyService())} icon={<PlusOutlined />} block>
                  添加服务
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  )
}
